import React, { PureComponent } from 'react';
import { PropTypes } from 'prop-types';
import { injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import ckeditorConfig, {
  ckEditorDefaultFontSizeStorageKey,
  ckEditorDefaultFontStorageKey,
  initializeCss,
} from '@/ckeditorConfig';
import classNames from 'classnames';
import { sanitize } from '@/shared/utils/htmlHelpers';
import userStorage from '@/shared/services/userStorage';

class RichTextEditor extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isReady: false,
    };
  }

  triggerOnChange = () => {
    const { onChange } = this.props;
    if (onChange && this.editor) {
      onChange(this.editor.getData());
    }
  };

  componentDidMount() {
    const { intl, id, initialValue, readonly, isFreeEmail } = this.props;
    const languageCode = intl.locale.split('-')[0];
    const config = {
      ...ckeditorConfig(isFreeEmail === false),
      fullPage: true,
      language: languageCode,
      readOnly: readonly,
    };

    // we only want to load the user selected font/fontsize when its a free email, otherwise we will override the template font
    initializeCss(isFreeEmail === false);

    // https://ckeditor.com/docs/ckeditor4/latest/guide/dev_installation.html
    // eslint-disable-next-line no-undef
    this.editor = CKEDITOR.replace(`editor-wrapper-${id}`, config);

    // save font and font size to local storage so it can be used to bootstrap editor based on user's preference
    this.editor.on('afterCommandExec', e => {
      if (e?.data?.name === 'font') {
        const fontName = e.data.commandData?.newStyle?._?.definition?.name;
        if (!fontName) return;
        userStorage.setItem(ckEditorDefaultFontStorageKey, fontName);
      }

      if (e?.data?.name === 'fontSize') {
        const fontSize = e.data.commandData?.newStyle?._?.definition?.name;
        if (!fontSize) return;
        userStorage.setItem(ckEditorDefaultFontSizeStorageKey, fontSize);
      }

      // we call this method so that the next time a ckeditor is loaded (within the same browser session), it will use the new font
      initializeCss();
    });

    // https://ckeditor.com/docs/ckeditor4/latest/examples/api.html
    this.editor.on('instanceReady', () => {
      // Set initial value
      this.editor.setData(sanitize(initialValue, true));
      this.editor.setReadOnly(readonly);
      ['dataReady', 'change', 'blur', 'saveSnapshot'].forEach(event => {
        this.editor.on(event, this.triggerOnChange);
      });
      this.setState({ isReady: true });
    });
  }

  componentDidUpdate(prevProps) {
    const { initialValue, readonly } = this.props;
    const { isReady } = this.state;

    if (prevProps.initialValue !== initialValue) {
      if (this.editor && isReady) {
        // Set editor content (replace current content).
        // https://ckeditor.com/docs/ckeditor4/latest/api/CKEDITOR_editor.html#method-setData
        this.editor.setData(sanitize(initialValue, true));
      }
    }

    if (prevProps.readonly !== readonly && this.editor) {
      if (isReady) {
        this.editor.setReadOnly(readonly);
      } else {
        this.editor.on('instanceReady', () => {
          this.editor.setReadOnly(readonly);
        });
      }
    }
  }

  componentWillUnmount() {
    if (this.editor) {
      // for some reason when destroying the editor, the html that is currently present is executed.
      // This can cause nasty behavior when there is XSS in the HTML so before destroying we wanna sanitize the content
      const data = this.editor.getData();
      this.editor.setData(sanitize(data));

      this.editor.destroy(false);
      this.editor = null;
    }
    this.setState({ isReady: false });
  }

  render() {
    const { id, noToolbar, readonly } = this.props;

    return (
      <div
        className={classNames({ 'no-toolbar-ckeditor': noToolbar, 'readonly-ckeditor': readonly })}
      >
        <div name={`editor-wrapper-${id}`} id={`editor-wrapper-${id}`} />
      </div>
    );
  }
}

RichTextEditor.propTypes = {
  initialValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  id: PropTypes.string,
  noToolbar: PropTypes.bool,
  readonly: PropTypes.bool,
};

RichTextEditor.defaultProps = {
  initialValue: '',
  id: 'default',
  noToolbar: false,
  readonly: false,
};

export default injectIntl(RichTextEditor);
